import React, { useState, useEffect } from 'react';
import Navbar from './Navbar';
import { Link } from 'react-router-dom';
import './Profile.css';
import user from './images/user.png';
import { auth, database, storage } from './firebase';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy } from '@fortawesome/free-solid-svg-icons';
import logo from './images/semicolon.png'

function Profile() {
  const [employmentStatus, setEmploymentStatus] = useState('');
  const [additionalInfo, setAdditionalInfo] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [profilePicture, setProfilePicture] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [loggedIn, setLoggedIn] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const navigate = useNavigate();
  const [userRank, setUserRank] = useState(0)
  const [userSolvedQuestionsCount, setUserSolvedQuestionsCount] = useState(0)
  const [userQuestions, setUserQuestions] = useState([]);
  const [selectedQuestionIndex, setSelectedQuestionIndex] = useState(null);
  const [topRankers, setTopRankers] = useState([]);

  const fetchQuestionDetails = (uid) => {
    const questionsRef = database.ref('users').child(uid).child('questions');
    questionsRef.on('value', (snapshot) => {
      const questionsData = snapshot.val();
      if (questionsData) {
        const questionsArray = Object.values(questionsData);
        // Set the questionCount for the current user
        setUserSolvedQuestionsCount(questionsArray.length);
  
        // Set all user questions in the state
        setUserQuestions(questionsArray);
      }
    });
  };  

  useEffect(() => {
    // Check if the user is already logged in
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        setLoggedIn(true);
        // Fetch user details from the database
        fetchUserDetails(user.uid);
        fetchUserRank(user.uid);
        fetchTopRankers();
      } else {
        setLoggedIn(false);
      }
    });

    return () => {
      unsubscribe();
      if (previewImage) {
        URL.revokeObjectURL(previewImage);
      }
    };
  }, [previewImage]);

  const fetchTopRankers = () => {
    const usersRef = database.ref('users');
    usersRef
      .orderByChild('questionsCount')
      .limitToLast(5) // Fetch the last 5 users with the highest questionsCount
      .once('value', (snapshot) => {
        const usersData = snapshot.val();
        if (usersData) {
          const topRankersData = Object.values(usersData);
          // Sort the top rankers in descending order of questionsCount
          topRankersData.sort((userA, userB) => {
            return (userB.questionsCount || 0) - (userA.questionsCount || 0);
          });
          setTopRankers(topRankersData);
        }
      });
  };

  const fetchUserDetails = (uid) => {
    const userRef = database.ref('users').child(uid);
    userRef.on('value', (snapshot) => {
      const userData = snapshot.val();
      if (userData) {
        setFirstName(userData.firstName || '');
        setLastName(userData.lastName || '');
        setEmail(userData.email || '');
        setEmploymentStatus(userData.employmentStatus || '');
        setAdditionalInfo(userData.additionalInfo || '');
        setProfilePicture(userData.profilePicture || null);

        if (userData.profilePicture) {
          setProfilePicture(userData.profilePicture);
        } else {
          setProfilePicture(null);
        }

        setUserSolvedQuestionsCount(userData.questionsCount || 0);
      }
    });
    fetchUserRank(uid);
    fetchQuestionDetails(uid);
  };

  const fetchUserRank = (uid) => {
    const usersRef = database.ref('users');
    usersRef.once('value', (snapshot) => {
      const usersData = snapshot.val();
      if (usersData) {
        // Create an array to hold user objects with uid property
        const usersArray = [];
        Object.keys(usersData).forEach((userKey) => {
          const userData = usersData[userKey];
          userData.uid = userKey;
          usersArray.push(userData);
        });
  
        // Sort users based on the number of questions submitted in descending order
        usersArray.sort((userA, userB) => {
          return (userB.questionsCount || 0) - (userA.questionsCount || 0);
        });
  
        // Find the current user's rank
        const currentUserIndex = usersArray.findIndex((user) => user.uid === uid);
        const currentUserRank = currentUserIndex + 1;
  
        // Update the state with the user's rank
        setUserRank(currentUserRank);
      }
    });
  };
  
  const handleFirstNameChange = (e) => {
    setFirstName(e.target.value);
  };

  const handleLastNameChange = (e) => {
    setLastName(e.target.value);
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
  };

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
  };

  const handleEmploymentStatusChange = (e) => {
    setEmploymentStatus(e.target.value);
  };

  const handleAdditionalInfoChange = (e) => {
    setAdditionalInfo(e.target.value);
  };

  const handleProfilePictureChange = (e) => {
    const file = e.target.files[0];
    const fileSize = file.size / 1024; // Convert file size to KB

    if (fileSize > 100) {
      // Show warning or error message to the user
      setErrorMessage('Please select an image of size less than 100KB');
      setProfilePicture(null);
      return;
    }

    setProfilePicture(file);

    if (file) {
      const imageUrl = URL.createObjectURL(file);
      setPreviewImage(imageUrl);
    } else {
      setPreviewImage(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const userCredential = await auth.createUserWithEmailAndPassword(
        email,
        password
      );

      const user = userCredential.user;

      // Save the form data to the Firebase Realtime Database with the user UID
      const userRef = database.ref('users').child(user.uid);
      userRef.set({
        firstName,
        lastName,
        email,
        employmentStatus,
        additionalInfo,
        profilePicture: profilePicture ? profilePicture.name : null,
      });

      if (profilePicture) {
        const storageRef = storage.ref().child(`profilePictures/${user.uid}`);
        storageRef.put(profilePicture).then(() => {
          // Upload completed successfully
          // Retrieve the download URL of the uploaded file
          storageRef.getDownloadURL().then((url) => {
            // Save the profile picture URL to the user's data in the Firebase Realtime Database
            userRef.update({ profilePicture: url });
          });
        }).catch((error) => {
          // Handle any errors during the upload process
          console.log(error);
        });
      }

      setFirstName('');
      setLastName('');
      setEmploymentStatus('');
      setAdditionalInfo('');
      setProfilePicture(null);
      setErrorMessage('');
      navigate('/login');
    } catch (error) {
      setErrorMessage(error.message);
    }  
  };

  const handleLogout = () => {
    auth.signOut();
    setLoggedIn(false);
  };

  const handleQuestionToggle = (index) => {
    setSelectedQuestionIndex((prevIndex) => (prevIndex === index ? null : index));
  };
  
  const getFormattedLanguageName = (language) => {
    const languageMap = {
      c_cpp: 'C++',
      javascript: 'JavaScript',
      java: 'Java',
      python: 'Python',
    };
  
    return languageMap[language] || language;
  };  

  const handleCopyCode = (code) => {
    // Create a temporary textarea element to copy the code to clipboard
    const textarea = document.createElement('textarea');
    textarea.value = code;
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
  };

  if (loggedIn) {
    return (
      <div className="user-profile">
        <Navbar />
        <div className='logo-container'>
          <Link to='/'>
          <img src={logo} className='logo' alt='logo' />
          </Link>
        </div>
        <div className='message'>
          <h1>
            SORRY !
          </h1>
          <p>
          Dear mobile user, we know you like convenience, but trust us when we say the desktop experience of this web app will blow your tiny mobile mind. Don't settle for less switch to desktop view and unleash your awesome powers!
          </p>
        </div>
        <div className='usr_prf'>
          <div className="profile-details">
            <h2>Welcome, {firstName} !!!</h2>
            <div className="profile-picture">
              <img
                src={profilePicture ? profilePicture : user}
                className="user_profile"
                alt="User Profile"
              />
            </div>

            <div className='user-details'>
              <p>
                <strong>First Name:</strong> {firstName}
              </p>
              <p>
                <strong>Last Name:</strong> {lastName}
              </p>
              <p>
                <strong>Email:</strong> {email}
              </p>
              <p>
                <strong>Employment Status:</strong> {employmentStatus}
              </p>
              {employmentStatus === 'Student' ? (
                <p>
                  <strong>School/College:</strong> {additionalInfo}
                </p>
              ) : (
                <p>
                  <strong>Company:</strong> {additionalInfo}
                </p>
              )}
              
            </div>

            <button className="logout-button" onClick={handleLogout}>
              Logout
            </button>
          </div>
          
          <div className='user-ranks'>
            <h2>
              <strong>Your Rank : {userRank}</strong> 
            </h2>
            <p>
              Questions Solved : {userSolvedQuestionsCount}
            </p>
            <h4>Questions:</h4>
            <ul>
            {userQuestions.map((question, index) => (
              <li key={index}>  
                <button onClick={() => handleQuestionToggle(index)} className='que_btn'>
                  {question.title}
                </button>
                {selectedQuestionIndex === index && (
                  <div className='que_det'>
                    <p>
                      <strong>Language:</strong> {getFormattedLanguageName(question.language)}
                    </p>                    
                    <p>
                      <strong>Code:</strong>
                    </p>
                    <div className="copy-icon">
                      <FontAwesomeIcon icon={faCopy} onClick={() => handleCopyCode(question.code)} />
                    </div>
                    <pre>{question.code}</pre>
                    
                  </div>
                )}
              </li>
            ))}
            </ul>
            {topRankers.length > 0 && (
              <div className="top-rankers">
                <h3>Top 5 Rankers</h3>
                <ul>
                  {topRankers.map((user, index) => (
                    <li key={index}>
                      <p>
                        <strong>Rank {index + 1} : </strong> {user.firstName} {user.lastName}
                      </p>
                      <p>
                        Questions Solved : {user.questionsCount || 0}
                      </p>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  } else {
  return (
    <div className="profile-container">
      <Navbar />
      <div className="profile-form">
        <div className="signup-form">
          <h2>Sign up for our platform</h2>
          <form onSubmit={handleSubmit}>
            <div className="profile-picture">
              <label htmlFor="profilePicture">
                <img src={previewImage || user} className="user_pic" alt="User Profile" />
              </label>
              <p className="profile_text">Profile Picture</p>
              <input
                className="pic_input"
                type="file"
                id="profilePicture"
                onChange={handleProfilePictureChange}
                accept="image/*"                
              />
            </div>
            <div className="full-name">
              <div className="name">
                <label htmlFor="firstName">First Name</label>
                <input
                  type="text"
                  id="firstName"
                  value={firstName}
                  onChange={handleFirstNameChange}
                  className="name-input"
                  required
                />
              </div>

              <div className="name">
                <label htmlFor="lastName">Last Name</label>
                <input
                  type="text"
                  id="lastName"
                  value={lastName}
                  onChange={handleLastNameChange}
                  className="name-input"
                  required
                />
              </div>
            </div>

            <div className="email_pass">
              <div className="email">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={handleEmailChange}
                  className="email-input"
                  required
                />
              </div>

              <div className="password">
                <label htmlFor="password">Password</label>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={handlePasswordChange}
                  className="password-input"
                  required
                />
              </div>
            </div>

            <div className="emp_status">
              <div className="employment">
                <label htmlFor="employmentStatus">Employment Status</label>
                <select
                  id="employmentStatus"
                  value={employmentStatus}
                  onChange={handleEmploymentStatusChange}
                  className="employment-input"
                  required
                >
                  <option value="">Select</option>
                  <option value="Student">Student</option>
                  <option value="Employee">Employee</option>
                </select>
              </div>

              {employmentStatus === 'Student' && (
                <div className="student">
                  <label htmlFor="school">School/College</label>
                  <input
                    type="text"
                    id="school"
                    value={additionalInfo}
                    onChange={handleAdditionalInfoChange}
                    className="student-input"
                    required
                  />
                </div>
              )}

              {employmentStatus === 'Employee' && (
                <div className="employee">
                  <label htmlFor="company">Company</label>
                  <input
                    type="text"
                    id="company"
                    value={additionalInfo}
                    onChange={handleAdditionalInfoChange}
                    className="employee-input"
                    required
                  />
                </div>
              )}
            </div>

            {errorMessage && (
              <p className="error-message">{errorMessage}</p>
            )}
            <button type="submit" className="sign-bt">
              Sign up
            </button>
          </form>

          <div className="login-section">
            <span className="login-txt">Already have an account?</span>
            <Link to="/login" className="link-style">
              <button className="login-button">Login</button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
}

export default Profile;
